import { FIXTURE_CADENCE_OPTIONS, WEEKDAYS } from './league-schedule-cadence.js';

const TYPE_LABELS = {
  singles: 'Singles',
  doubles: 'Doubles',
  team: 'Team',
  beer: 'Beer',
};

const CHECKOUT_LABELS = {
  'double-out': 'Double out',
  'straight-out': 'Straight out',
  'master-out': 'Master out',
};

export function weekdayLabel(key) {
  return WEEKDAYS.find((d) => d.key === key)?.label || String(key || '—');
}

export function cadenceLabel(id) {
  const row = FIXTURE_CADENCE_OPTIONS.find((c) => c.id === id);
  return row?.label || String(id || 'weekly').replace(/_/g, ' ');
}

function checkoutLabel(value) {
  return CHECKOUT_LABELS[String(value || 'double-out')] || String(value || 'double-out');
}

function gameRowSummary(row, index) {
  const type = TYPE_LABELS[String(row?.type || 'singles').toLowerCase()] || row?.type || 'Game';
  const format = String(row?.format || '501');
  const legs = Number(row?.bestOfLegs) || 3;
  const label = String(row?.label || '').trim();
  const co = row?.checkout ? checkoutLabel(row.checkout) : 'Profile default';
  const maxD = row?.maxDartsPerLeg != null && row.maxDartsPerLeg !== '' ? `${row.maxDartsPerLeg} darts/leg` : '—';
  const title = label || `${type} ${index + 1}`;
  return `${row?.order ?? index + 1}. ${title} — ${type}, ${format}, best of ${legs}, ${co}, max ${maxD}`;
}

export function serializeGameTemplateRow(row) {
  return {
    order: Number(row?.order) || 1,
    type: String(row?.type || 'singles'),
    format: String(row?.format || '501'),
    bestOfLegs: Number(row?.bestOfLegs) || 3,
    label: String(row?.label || '').trim().slice(0, 80),
    checkout: String(row?.checkout || '').trim(),
    maxDartsPerLeg:
      row?.maxDartsPerLeg != null && row.maxDartsPerLeg !== '' ? Number(row.maxDartsPerLeg) : null,
  };
}

export function leagueRulesExportPayload(form) {
  const defaultStartingScore = Number(form.defaultStartingScore);
  return {
    displayName: String(form.displayName || '').trim(),
    notes: String(form.notes || '').trim(),
    defaultCheckout: form.defaultCheckout || 'double-out',
    defaultStartingScore: Number.isFinite(defaultStartingScore) ? defaultStartingScore : 501,
    defaultMaxDartsPerLeg:
      form.defaultMaxDartsPerLeg != null && form.defaultMaxDartsPerLeg !== ''
        ? Number(form.defaultMaxDartsPerLeg)
        : null,
    matchStartTime: form.matchStartTime || '19:30',
    fixtureCadence: form.fixtureCadence || 'weekly',
    fixtureWeekday: form.fixtureWeekday || 'thu',
    fixtureWeekday2: form.fixtureWeekday2 || 'tue',
    gameTemplate: (Array.isArray(form.gameTemplate) ? form.gameTemplate : []).map(serializeGameTemplateRow),
  };
}

export function buildLeagueRulesSummaryLines(form, opts = {}) {
  const lines = [];
  const name = String(form?.displayName || opts.fallbackName || 'Untitled rules').trim() || 'Untitled rules';
  lines.push(`Profile: ${name}`);
  if (form?.notes) lines.push(`Notes: ${String(form.notes).trim()}`);
  lines.push(`Default checkout: ${checkoutLabel(form?.defaultCheckout)}`);
  if (form?.defaultStartingScore != null && form.defaultStartingScore !== '') {
    lines.push(`Default starting score: ${form.defaultStartingScore}`);
  }
  if (form?.defaultMaxDartsPerLeg != null && form.defaultMaxDartsPerLeg !== '') {
    lines.push(`Default max darts per leg: ${form.defaultMaxDartsPerLeg}`);
  }
  lines.push(`Match start: ${form?.matchStartTime || '19:30'}`);
  const cadence = form?.fixtureCadence || 'weekly';
  if (cadence === 'twice_weekly') {
    lines.push(
      `Fixture cadence: ${cadenceLabel(cadence)} (${weekdayLabel(form?.fixtureWeekday)} + ${weekdayLabel(form?.fixtureWeekday2)})`,
    );
  } else {
    lines.push(`Fixture cadence: ${cadenceLabel(cadence)} on ${weekdayLabel(form?.fixtureWeekday)}`);
  }
  const games = Array.isArray(form?.gameTemplate) ? form.gameTemplate : [];
  lines.push(`Match program: ${games.length} game${games.length === 1 ? '' : 's'}`);
  games.forEach((row, i) => lines.push(`  ${gameRowSummary(row, i)}`));
  return lines;
}

export function buildLeagueRulesSummaryText(form, opts = {}) {
  return buildLeagueRulesSummaryLines(form, opts).join('\n');
}

const WEEKDAY_LABELS = Object.fromEntries(WEEKDAYS.map((d) => [d.key, d.label]));

export function buildLeagueBuilderTemplateSummaryLines(tpl, ctx = {}) {
  const lines = [];
  const name = String(tpl?.name || 'Untitled template').trim();
  lines.push(`Template: ${name}`);
  if (tpl?.description) lines.push(String(tpl.description).trim());
  if (tpl?.season) lines.push(`Season label: ${tpl.season}`);
  const payload = tpl?.payload || {};
  if (payload.leagueName) lines.push(`League name hint: ${payload.leagueName}`);
  if (payload.leagueKey) lines.push(`League ID hint: ${payload.leagueKey}`);
  if (payload.startDate) lines.push(`Suggested start date: ${payload.startDate}`);
  if (payload.startTime) lines.push(`Start time: ${payload.startTime}`);
  const freq = payload.frequency === 'twice' ? 'Twice per week' : 'Once per week';
  const d1 = WEEKDAY_LABELS[payload.weekday1] || payload.weekday1 || 'Thursday';
  if (payload.frequency === 'twice') {
    const d2 = WEEKDAY_LABELS[payload.weekday2] || payload.weekday2 || 'Tuesday';
    lines.push(`Schedule: ${freq} — ${d1} & ${d2}`);
  } else {
    lines.push(`Schedule: ${freq} on ${d1}`);
  }
  lines.push(`Round-robin cycles: ${Math.max(1, Number(payload.roundRobinCycles) || 1)}`);
  lines.push(`Team selection: ${payload.teamMode === 'manual' ? 'Manual pick' : 'Entire pool'}`);
  lines.push(`Home/away balancing: ${payload.balanceHomeAway !== false ? 'On' : 'Off'}`);
  lines.push(`Avoid venue clashes: ${payload.avoidVenueClashes !== false ? 'On' : 'Off'}`);
  const custom = Array.isArray(payload.customRows) ? payload.customRows : [];
  if (custom.length) {
    lines.push(`Custom rows included: ${custom.length} (breaks, events, holidays, or manual fixtures)`);
  }
  const teamCount = Array.isArray(payload.selectedTeamIds) ? payload.selectedTeamIds.length : 0;
  if (teamCount) lines.push(`Saved team IDs in template: ${teamCount} (re-select teams after import if needed)`);
  if (ctx.rulesName) lines.push(`Linked rules profile: ${ctx.rulesName}`);
  else if (payload.leagueRulesId) lines.push(`Linked rules profile ID: ${payload.leagueRulesId}`);
  if (tpl?.builtin) lines.push('Built-in example template — load with Use template, then adjust teams and dates.');
  return lines;
}

export function buildLeagueBuilderTemplateSummaryText(tpl, ctx = {}) {
  return buildLeagueBuilderTemplateSummaryLines(tpl, ctx).join('\n');
}

export function leagueBuilderTemplateExportPayload(form, templatePayload, extra = {}) {
  return {
    name: String(extra.name || form?.templateName || form?.leagueName || 'League template').trim(),
    description: String(extra.description || '').trim(),
    leagueKey: String(form?.leagueKey || '').trim().toUpperCase(),
    season: String(form?.season || '').trim(),
    payload: templatePayload,
  };
}
